/**
 * Custom validator implementation that defines our message rules and custom validations.
 *
 * Validators defined here can be set on inputs in a DFValidator container by setting the following attributes on those fields:
 *
 * data-illegal: attribute's value should be a list of characters that are illegal in this field
 * data-no-space: attribute just needs to exist
 * data-range: attribute needs a true value: validates that input is between -2147483648 and 2147483647
 * data-decimal: attribute needs a true value
 * data-integer: attribute needs a true value
 * data-clean-url: attribute just needs to exist
 *
 * We also allow overriding the messages for some of the built-in kendo validator rules. The following
 * attributes can be set to a locale key to override the message for the corresponding rule:
 *
 * data-pattern-message: accepts a locale key for the error message of the data-pattern validator
 */
import locale from "common/managers/locale";
import macroValidator from "./macroValidator";
import DuplicateValidator from "./DuplicateValidator";
import email from "./emailValidator";
import "common/bindings/requestError";
import stringUtil from "common/helper/stringUtil";
var DFValidator = kendo.ui.Validator.extend({

    init: function(element, config) {
        var me = this;
        this._cache = {};
        var baseConfig = {
            messages: {
                notVariable: function(input) {
                    return locale.get("enter.variable.name");
                },
                pattern: function(input) {
                    var key = input.attr("data-pattern-message") || "validation.pattern";
                    return locale.get(key);
                },
                illegal: function(input) {
                    return locale.get(input.attr("data-illegal-message") || "validation.pattern");
                },
                nospace: function(input) {
                    return locale.get("validation.noSpace");
                },
                range: function(input) {
                    return locale.get("numeric.range");
                },
                decimal: function(input) {
                    return locale.get("numeric.decimal.validation");
                },
                integer: function(input) {
                    return locale.get("numeric.integer.validation");
                },

                min: function(input) {
                    // dfnumerictextbox-specific validation message for reporting when a number is outside the widget-impl-defined range
                    if (input.is("[data-role='dfnumerictextbox']") || input.is("[data-role='numerictextinput']")) {
                        var widget = kendo.widgetInstance(input);
                        var message = input.attr("data-message-min");
                        return locale.get(message ? message : "validation.generic.min", locale.get("validation.generic.field"), widget.options.min);
                    }

                    if (input.attr("data-message-min") || input.attr("data-message")) {
                        return locale.get(input.attr("data-message-min") || input.attr("data-message"), input.attr("name"));
                    }
                    return "";
                },

                /**
                 * If validation fails, a message is returned telling the user what failed
                 * The message can be defined on the elements "data-message-max" attribute or a generic message will be returned
                 * @param input
                 * @returns {string}
                 */
                max: function(input) {
                    var widget = kendo.widgetInstance(input);
                    var message = input.attr("data-message-max");
                    return locale.get(message ? message : "validation.generic.max", locale.get("validation.generic.field"), widget.options.max);
                },

                required: function(input) {
                    if (input.attr("data-message")) {
                        return locale.get(input.attr("data-message"), input.attr("name"));
                    }
                    return locale.get("validation.required");
                },
                minlength: function(input) {
                    return locale.get("validation.minLength", input.attr("data-min-length"));
                },
                macro: function(input) {
                    return macroValidator.message(input.val());
                },
                duplicate: function(input) {
                    // each field defines its own message, expected to be a locale key
                    var ret = input.data("duplicateMessage");
                    if (!ret || ret == "") {
                        // default to a simple message
                        ret = locale.get("validation.duplicate");
                    } else {
                        ret = locale.get(ret);
                    }
                    return ret;
                },
                dfdate: function(input) {
                    return locale.get("date.invalid", locale.getDateFormat().toUpperCase());
                },
                email: function(input) {
                    return locale.get("email.invalid");
                },
                server: function(input) {
                    return input.data("df-server-message");
                },
                greaterThan: function(input) {
                    if (input.attr("data-greater-than-message")) {
                        return locale.get(input.attr("data-greater-than-message"));
                    }
                    return "";
                },
                unique: function(input) {
                    if (input.attr("data-unique-message")) {
                        return locale.get(input.attr("data-unique-message"));
                    } else {
                        return locale.get("validation.duplicate");
                    }
                },
                uniqueTwo: function(input) {
                    if (input.attr("data-unique-two-message")) {
                        return locale.get(input.attr("data-unique-two-message"));
                    } else {
                        return locale.get("validation.duplicate");
                    }
                },
                future: function(input) {
                    if (input.attr("data-future-message")) {
                        return locale.get(input.attr("data-future-message"));
                    } else {
                        return locale.get("validation.future");
                    }
                },
                cleanUrl: function(input) {
                    return locale.get("validation.cleanUrl");
                },
                regex: function() {
                    return locale.get("validation.regex");
                }
            },

            rules: {
                notVariable: function(input) {
                    if (input.is("[data-role=newvariablepicker]")) {
                        var now = kendo.widgetInstance(input).value();
                        return !(/^\$\{.*\}$/.test(now));
                    }
                    return true;
                },
                email: function(input) {
                    if (input.is("[type=email]")) {
                        return email.validate(input.val());
                    }
                    return true;
                },
                illegal: function(input) {
                    if (input.is("[data-illegal]")) {
                        const illegal = [...input.attr("data-illegal")];
                        const val = input.val();
                        for(const char of illegal) {
                            if(val.includes(char)) {
                                return false;
                            }
                        }
                    }
                    return true;
                },
                nospace: function(input) {
                    if (input.is("[data-no-space]")) {
                        var value = input.val();
                        return !/\s/.test(value);
                    }
                    return true;
                },
                range: function(input) {
                    if (input.attr("data-range")) {
                        var value = kendo.parseFloat(input.val());
                        if (value === null) {
                            return true;
                        }
                        return value <= 2147483647 && value >= -2147483648;
                    }
                    return true;
                },
                decimal: function(input) {
                    if (input.attr("data-decimal")) {
                        var val = input.val();
                        if (val.length == 0) {
                            return true;
                        }
                        return /^[-+]?[0-9]*\.?[0-9]*$/.test(val);
                    }
                    return true;
                },
                // data-integer attribute needs a true value to enable the integer validator
                integer: function(input) {
                    if (input.attr("data-integer")) {
                        var val = input.val();
                        if (val.length == 0) {
                            return true;
                        }
                        return /^[-+]?[0-9]+$/.test(val);
                    }
                    return true;
                },

                required: function(input) {
                    if (!input.attr("required") || input.attr("required") === "false") {
                        return true;
                    }

                    var widget;
                    if (!input.attr("data-ignore-widget")) {
                        widget = kendo.widgetInstance(input);
                    }
                    var val;
                    if (widget && widget.valueForValidation) {
                        val = widget.valueForValidation();
                    } else if (widget && widget.value) {
                        val = widget.value();
                    } else if (input.is("[type='checkbox']")) {
                        val = input.is(":checked");
                    } else {
                        val = input.val();
                    }

                    if (val !== undefined && val !== null && val.trim) {
                        val = val.trim();
                        if (val.length < 1) {
                            return false;
                        }
                    }
                    if (input.attr("data-fuzzy-required")) {
                        return !(val === "" || !val);
                    } else {
                        return !(val === "" || val === false || val === null || val === undefined);
                    }
                },
                min: function(input) {
                    // dfnumerictextbox-specific validation for validating when a number is outside the widget-impl-defined range
                    var widget = kendo.widgetInstance(input);
                    if (input.is("[data-role='dfnumerictextbox']") || input.is("[data-role='numerictextinput']")) {
                        if (widget && widget.options && widget.options.min !== undefined && widget.options.min !== null) {
                            return widget.value() >= widget.options.min;
                        }
                        return true;
                    }

                    if (input.attr("min") === undefined) {
                        return true;
                    }
                    var val;
                    if (widget && widget.value) {
                        val = widget.value();
                    } else {
                        val = input.val();
                    }
                    return val >= input.attr("min")
                },

                /**
                 * Ensures the value of the input is less than or equal to the input's widget's "max" option value
                 * @param input
                 * @returns {boolean}
                 */
                max: function(input) {
                    if (!(input.is("[data-role='dfnumerictextbox']") || input.is("[data-role='numerictextinput']"))) {
                        return true;
                    }
                    var widget = kendo.widgetInstance(input);
                    if (widget && widget.options && widget.options.max !== undefined && widget.options.max !== null) {
                        return widget.value() <= widget.options.max;
                    }
                    return true;
                },

                minlength: function(input) {
                    if (input.is("[data-min-length]")) {
                        var widget;
                        if (!input.attr("data-ignore-widget")) {
                            widget = kendo.widgetInstance(input);
                        }
                        var val;
                        if (widget && widget.valueForValidation) {
                            val = widget.valueForValidation();
                        } else if (widget && widget.value) {
                            val = widget.value();
                        } else {
                            val = input.val();
                        }
                        var minLength = input.attr("data-min-length");

                        if (val !== null && val.trim) {
                            val = val.trim();
                            return stringUtil.length(val) >= minLength;
                        }
                    } else {
                        return true;
                    }
                },
                macro: function(input) {
                    if (input.is("[data-role='macroarea']") || input.is("[data-role='formdesignermacroarea']")) {
                        var widget = kendo.widgetInstance(input);
                        var silent = me.silence();
                        // If the widget has been removed from screen, we either no longer care about
                        // this validation, or assume a server validation will catch it later.
                        // The macro validation to the server is a best effort, but not fail-proof.
                        if (widget !== undefined) {
                            var paramType = widget.options.usePromptSyntax ? "PROMPT" : "VARIABLE";
                            return macroValidator.validate(widget.value(), paramType, function() {
                                var silent_now = me.silence();
                                me.silence(silent);
                                me.validateInput(input);
                                me.silence(silent_now);
                            });
                        }
                    } else {
                        return true;
                    }
                },

                duplicate: DuplicateValidator,

                dfdate: function(input) {
                    var ret = true;
                    if ((input.is("[data-role='dfdatepicker']:not([data-skip-dfdate])")) || input.is("[data-dfdate]")) {
                        var isRequired = input.attr("required") !== undefined,
                            rawVal = $(input).val(),
                            widget = kendo.widgetInstance(input),
                            widgetVal = widget.value();

                        // Required field validation always runs before this.
                        // If the field is required, and the user provided nothing at all, we will not get here.
                        // We need to determine whether the user entered a valid date into the field.

                        // When the field is required, we can rely on the widget's value() method to give us that info.
                        // Otherwise, we can look at the raw value and parse it to see whether the user entered something weird.

                        if (isRequired) {
                            ret = typeof(widgetVal) === "number";
                        } else {
                            if (rawVal && rawVal !== "") {
                                rawVal = kendo.parseDate(rawVal, "d");
                                if (!rawVal || (widgetVal !== null && typeof(widgetVal) !== "number")) {
                                    ret = false;
                                }
                            }
                        }
                    }
                    return ret;
                },

                server: function(input) {
                    var valid = !input.is("[data-df-server-invalid]");
                    // immediately remove the attribute we just looked for, otherwise the UI can never get into
                    // a valid state again.  There might be a better way to handle this.
                    // Current behavior keeps the message up for the given field until the user enters the field
                    // and loses focus (or does another action that causes validation).  The user doesn't need
                    // to change the field value, but that should be acceptable.  They will submit the form again,
                    // and get the error again.
                    input.removeAttr("data-df-server-invalid");
                    return valid;
                },

                greaterThan: function(input) {

                    // currently only handles comparing input against another input identified by ID

                    // look for the other's ID
                    if (input.attr("data-greater-than-id") === undefined) {
                        return true;
                    }

                    // determine the input's value
                    var widget = kendo.widgetInstance(input);
                    var val = (widget && widget.value) ? widget.value() : input.val();
                    if (val == null) {
                        return true;
                    }

                    // determine the other's value
                    var otherInput = $("#" + input.attr("data-greater-than-id"));
                    var otherWidget = kendo.widgetInstance(otherInput);
                    var otherVal = (otherWidget && otherWidget.value) ? otherWidget.value() : otherInput.val();

                    // compare values
                    return val > otherVal;
                },

                /**
                 * Ensure that the value is unique by contacting server.
                 * @param input
                 * @returns {*}
                 */
                unique: function(input) {
                    if (input.is("[data-unique]")) {
                        var current = input.val();
                        var cached = input.attr("data-unique-start");
                        var extra = input.attr("data-unique");
                        if (current && current != cached && current == me._cache.lastName) {
                            return me._cache.lastResult;
                        } else if (current && current != cached) {
                            me.options.unique(current, extra).done(function(res) {
                                me._cache.lastName = current;
                                me._cache.lastResult = res;
                                me.validateInput(input);
                            });
                        }
                    }
                    return true;
                },

                uniqueTwo: function(input) {
                    if (input.is("[data-unique-two]")) {
                        var current = input.val();
                        var cached = input.attr("data-unique-two-start");
                        var extra = input.attr("data-unique-two");
                        if (current && current != cached && current == me._cache.lastName) {
                            return me._cache.lastResult;
                        } else if (current && current != cached) {
                            me.options.uniqueTwo(current, extra).done(function(res) {
                                me._cache.lastName = current;
                                me._cache.lastResult = res;
                                me.validateInput(input);
                            });
                        }
                    }
                    return true;
                },

                future: function(input) {
                    if (!input.is("[data-future]")) {
                        return true;
                    }
                    var widget = kendo.widgetInstance(input);
                    if (!widget || !widget.value) {
                        return true;
                    }
                    return (widget.value() > new Date());
                },

                /**
                 * Validates a "clean" URL. Supported characters in URLs at this time are only
                 * lower case a-z and hyphen, but this should be extended here as needed.
                 * NOTE: There is a urlHelper class used in Gateway that should be changed as well, if this
                 * validator changes.
                 * @param input
                 */
                cleanUrl: function(input) {
                    if (!input.is("[data-clean-url]")) {
                        return true;
                    }

                    var current = input.val();
                    // NOTE!! that the default message for this validation rule should be changed in the locale string if this regex changes!
                    var allowedChars = new RegExp("^[a-z0-9\-]*$");
                    return allowedChars.test(current);
                },

                regex: function(input) {
                    if(!input.is("[data-regex]")) {
                        return true;
                    }
                    try {
                        new RegExp(input.val());
                        return true;
                    } catch(ex) {
                        return false;
                    }
                }
            }
        };
        if (!config) {
            config = {};
        }
        kendo.ui.Validator.fn.init.call(this, element, $.extend(true, {},
            baseConfig,
            config
        ));
        this.wrapper = this.element;
        if(this.options.validateAllRequired) {
            this._inputSelector += ",[required]";
        }

        this.element.on("click", ".validationWrap", function(e) {
            $(e.currentTarget).toggleClass("validationDetails");
        });
    },

    options: {
        name: "DFValidator",
        errorTemplate: '<div class="validationWrap"><span class="k-widget k-tooltip k-tooltip-validation">' +
            '<span class="k-icon k-warning" aria-hidden="true"> </span> <span class="text">#=message#</span></span></div>',

        validateAllRequired: false,

        //A webservice that can be called to test if something is unique by the unique validator
        unique: null,
        //A second webservice that can be called to test for uniqueness, if 2 fields are needed to be unique on a single form
        uniqueTwo: null
    },

    setOptions() {
        //Workaround a Kendo breaking change.
        //If Validator.setOptions is called, the whole widget will be destroyed and reccreated
        //That has many extremely bad side effects.
        //We currently need to use setOptions in some sub classes, but don't need to use setOptions for Validator.
        kendo.ui.Widget.fn.setOptions.apply(this, arguments);
    },

    validateWithoutFocus() {
        return kendo.ui.Validator.fn.validate.apply(this, arguments);
    },

    validate: function() {
        var res = kendo.ui.Validator.fn.validate.apply(this, arguments);
        if (!this.silence()) {
            this.focusInvalid();
        }
        return res;
    },

    focusInvalid() {
        var focus = this.element.find("[data-df-valid='invalid']").first();
        var widget = kendo.widgetInstance(focus);
        if (widget && widget.focus) {
            widget.focus();
        } else {
            focus.focus();
        }
    },

    /**
     * Clear any cached validation info.
     */
    clearCache: function() {
        this._cache = {};
    },

    /**
     * Validates the full form without modifying the UI in anyway. Overrides the input selector (temporarily)
     * to enable validation on disabled and read only inputs.
     *
     * @returns {boolean}
     */
    silentValidate: function() {
        var cache = this.silence();
        this.silence(true);
        var oldInputSelector = this._inputSelector;
        this._inputSelector = ":input:not(:button,[type=submit],[type=reset])";
        var res = kendo.ui.Validator.fn.validate.apply(this, arguments);
        this._inputSelector = oldInputSelector;
        this.silence(cache);
        return res;
    },

    /**
     * Validate a single input wihtout modifying the UI in any way
     * @returns {boolean}
     */
    silentValidateInput: function(el) {
        var cache = this.silence();
        this.silence(true);
        var res = this.validateInput.apply(this, arguments);
        this.silence(cache);
        return res;
    },

    /**
     * Stop/Start validation messages from being modified
     * @param sil
     * @returns {boolean}
     */
    silence: function(sil) {
        if (arguments.length == 1) {
            this._silenceMessages = sil ? true : false;
        }
        return this._silenceMessages === true;
    },


    validateInput: function(el) {
        var element = $(el);
        var res;
        if (this.silence()) {
            //This uses an internal kendo function
            //it just loops through this.options.rules and calls each rule on the element until it fails one
            res = this._checkValidity(element).valid;
        } else {
            res = kendo.ui.Validator.fn.validateInput.apply(this, arguments)
            if (res) {
                this._setValidationDescribedBy(element, "");
            }
        }
        element.attr("data-df-valid", res ? "valid" : "invalid");
        this.trigger("validateInput", {
            input: element,
            inputValid: res,
            valid: res && this.isValid()
        });
        return res;
    },

    /**
     * Returns the current validation state of the form.
     * This only works if validate/silentValidate was called at least once, and validateOnBlue=true
     * @returns {boolean}
     */
    isValid: function() {
        var invalid = this.element.find("[data-df-valid='invalid']");
        return invalid.length == 0;
    },

    _setValidationDescribedBy: function(input, id) {
        var widget = kendo.widgetInstance(input);
        if (widget && widget.setDescribedBy) {
            widget.setDescribedBy("VALIDATION", id);
        }
    },

    _decorateMessageContainer: function(container, fieldName) {
        kendo.ui.Validator.fn._decorateMessageContainer.apply(this, arguments);
        container.removeAttr("role");
        var id = fieldName + "_DFValidator_message";
        container.attr("id", id);
        this._setValidationDescribedBy(this.element.find("[name='" + fieldName + "']"), id);
    }

});

kendo.ui.plugin(DFValidator);
export default DFValidator;
